/**
 * GenerateIdeas.js — Orchestrator
 * Manages mode, topic, platform, saved ideas, and history state.
 */
import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { Box, Typography, Alert, Snackbar, alpha, useTheme } from '@mui/material';
import IdeasInput from './ideas/IdeasInput';
import IdeasResults from './ideas/IdeasResults';
import IdeasEmptyState from './ideas/IdeasEmptyState';
import { ideasApi } from '../services/api';

const GenerateIdeas = React.memo(({ isPro = false, error: externalError }) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const [topic, setTopic] = useState('');
    const [platform, setPlatform] = useState(null);
    const [mode, setMode] = useState('smart');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [ideaResult, setIdeaResult] = useState(null);

    // Save / History state
    const [savedIdeas, setSavedIdeas] = useState([]);
    const [ideasHistory, setIdeasHistory] = useState([]);
    const [activeTab, setActiveTab] = useState('Generate');

    const cardStyle = useMemo(() => ({
        p: 3,
        borderRadius: '8px',
        background: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: isDark ? '0 4px 24px rgba(0,0,0,0.35)' : '0 2px 16px rgba(0,0,0,0.06)',
    }), [theme, isDark]);

    const [snack, setSnack] = useState('');

    // Load saved ideas + history on mount
    useEffect(() => {
        let mounted = true;
        ideasApi.getSaved().then(d => mounted && setSavedIdeas(d?.saved_ideas || [])).catch(() => {});
        ideasApi.getHistory().then(d => mounted && setIdeasHistory(d?.history || [])).catch(() => {});
        return () => { mounted = false; };
    }, []);

    const handleTopicChange = useCallback((e) => setTopic(e.target.value), []);
    const handlePlatformChange = useCallback((val) => setPlatform(val), []);
    const handleModeChange = useCallback((val) => setMode(val), []);

    const handleSubmit = useCallback(async () => {
        const effectiveTopic = topic.trim();
        // Double Down and Trending+You don't need a topic
        if (!effectiveTopic && !['double_down', 'trending_you', 'fill_gap'].includes(mode)) return;
        setLoading(true);
        setError('');
        try {
            const data = await ideasApi.generate(effectiveTopic, platform, 5, mode);
            setIdeaResult(data);
            setActiveTab('Generate');
            // Refresh history
            ideasApi.getHistory().then(d => setIdeasHistory(d?.history || [])).catch(() => {});
        } catch (err) {
            setError(err?.message || 'Failed to generate ideas. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [topic, platform, mode]);

    const isValid = useMemo(() => {
        if (loading) return false;
        if (['double_down', 'trending_you'].includes(mode) && isPro) return true;
        if (mode === 'fill_gap' && isPro) return true; // topic optional
        return topic.trim().length > 2;
    }, [topic, mode, isPro, loading]);

    const handleSaveIdea = useCallback(async (idea, sourceTopic, sourceMode) => {
        try {
            await ideasApi.save(idea, sourceTopic, sourceMode);
            const d = await ideasApi.getSaved();
            setSavedIdeas(d?.saved_ideas || []);
        } catch {
            setSnack('Failed to save idea. Please try again.');
        }
    }, []);

    const handleDeleteSaved = useCallback(async (ideaId) => {
        try {
            await ideasApi.deleteSaved(ideaId);
            setSavedIdeas(prev => prev.filter(s => s.id !== ideaId));
        } catch {
            setSnack('Failed to remove idea. Please try again.');
        }
    }, []);

    const pendingRegenRef = useRef(false);
    const handleRegenerate = useCallback((historyEntry) => {
        setTopic(historyEntry.topic || '');
        setPlatform(historyEntry.platform || null);
        setMode(historyEntry.mode || 'smart');
        setActiveTab('Generate');
        pendingRegenRef.current = true;
    }, []);

    useEffect(() => {
        if (pendingRegenRef.current && !loading) {
            pendingRegenRef.current = false;
            handleSubmit();
        }
    }, [topic, platform, mode, loading, handleSubmit]);

    const displayError = error || externalError;

    return (
        <>
            <Box>
            {/* Page Header */}
            <Box sx={{ mb: 6 }}>
                <Typography variant="h3" sx={{ fontWeight: 900, letterSpacing: '-0.03em', mb: 1.5 }}>
                    Content Idea Engine
                </Typography>
                <Typography variant="body1" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                    Generate platform-native content strategies in seconds.
                </Typography>
            </Box>

            {displayError && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: '12px', border: `1px solid ${alpha('#ef4444', 0.2)}`, background: alpha('#ef4444', 0.05) }}>
                    {displayError}
                </Alert>
            )}

            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', lg: 'row' },
                gap: 3,
                alignItems: 'stretch',
            }}>
                {/* Input Panel — fixed width */}
                <Box sx={{ flex: '0 0 auto', width: { xs: '100%', lg: 380 } }}>
                    <IdeasInput
                        topic={topic}
                        onTopicChange={handleTopicChange}
                        platform={platform}
                        onPlatformChange={handlePlatformChange}
                        mode={mode}
                        onModeChange={handleModeChange}
                        isPro={isPro}
                        onSubmit={handleSubmit}
                        loading={loading}
                        isValid={isValid}
                        card={cardStyle}
                    />
                </Box>

                {/* Results Panel */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    {ideaResult || savedIdeas.length > 0 || ideasHistory.length > 0 ? (
                        <IdeasResults
                            ideaResult={ideaResult}
                            card={cardStyle}
                            savedIdeas={savedIdeas}
                            onSaveIdea={handleSaveIdea}
                            onDeleteSaved={handleDeleteSaved}
                            ideasHistory={ideasHistory}
                            onRegenerate={handleRegenerate}
                            activeTab={activeTab}
                            onTabChange={setActiveTab}
                        />
                    ) : (
                        <Box sx={{
                            ...cardStyle,
                            minHeight: 450,
                            display: 'flex',
                            flexDirection: 'column',
                            flex: 1,
                        }}>
                            <IdeasEmptyState />
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>

            <Snackbar
                open={!!snack}
                autoHideDuration={4000}
                onClose={() => setSnack('')}
                message={snack}
            />
        </>
    );
});

export default GenerateIdeas;
